import * as React from 'react';

import { TabBar } from '@longline/aqua-ui/controls/TabBar';

import { useUsersContext } from '../IUsersContext';
import { Pane } from '../../../ui/Pane';
import { TResourceState } from '../../../types/TResourceState';
import { IUser } from '../../../api/models';
import { ISystemFilter, SystemFilter } from "../../../filters/SystemFilter";
import { SystemSelection } from '../SystemSelection';
import { EditUserForm } from './EditUserForm';

const EditUser = () => {
  const ctx = useUsersContext();

  // The mode === 'edit' switch in UsersManager guarantees ctx.user is
  // populated before this component is rendered.
  const initialUser = ctx.user;
  const user = React.useRef<IUser | null>(initialUser ? {...initialUser} : null);
  const [isValid, setIsValid] = React.useState<boolean>(true);
  const [readyState, setReadyState] = React.useState<TResourceState>('ready');
  const [filter, setFilter] = React.useState<ISystemFilter>({});
  const [dirty, setDirty] = React.useState(false);

  const controller = React.useRef<AbortController | null>(null);

  if (!ctx.user || !user.current) return null;
  const ctxUser = ctx.user;
  const userRef = user.current;

  const handleChange = (value: IUser, forceUpdate: boolean) => {
    Object.assign(userRef, value);
  }

  const handleSubmit = async() => {
    if(readyState == 'loading') return;

    ctx.setTab('data');

    if(!isValid) {
      setDirty(true);
      return;
    }

    // Systems list must be a flat list of UUIDs:
    const u = {
      ...userRef,
      systems: userRef.systems.filter(s => (s as any).checked).map(s => s.uuid)
    }

    setReadyState('loading');
    controller.current = new AbortController();
    setReadyState(await ctx.updateUser(u as any, controller.current.signal));
  }

  return (
    <>
      <Pane.Heading>
        {ctx.tab === 'data' && <Pane.Title>{ctxUser.name}</Pane.Title>}
        {ctx.tab === 'systems' && <SystemFilter filter={filter} onChange={setFilter}/>}
      </Pane.Heading> 
      <Pane.Subheader expandable>
        Edit user
      </Pane.Subheader>

     <TabBar active={ctx.tab === 'systems' ? 1 : 0} line>
        <TabBar.Tab onClick={() => ctx.setTab('data')}>Data</TabBar.Tab>
        <TabBar.Tab onClick={() => ctx.setTab('systems')}>Systems</TabBar.Tab>
      </TabBar>

      <div style={{display: ctx.tab === 'data' ? 'block' : 'none', flex: 1 }}>
        <Pane.Body>
          <EditUserForm
            dirty={dirty}
            user={ctxUser}
            onChange={handleChange}
            onValidate={(v: boolean) => setIsValid(v)}
          />
        </Pane.Body> 
      </div>

      <div style={{display: ctx.tab === 'systems' ? 'block' : 'none', flex: 1 }}>
        <SystemSelection user={ctxUser} onChange={handleChange} filter={filter} onClearFilter={() => setFilter({})}/>
      </div>      

      <Pane.Footer>
        <Pane.CancelButton onClick={() => ctx.setMode('view')}/>
        <Pane.SubmitButton onClick={() => handleSubmit()} working={readyState == 'loading'} disabled={readyState == 'loading'}/>
      </Pane.Footer>
    </>
  )
}

export { EditUser }
